import React, { useContext } from "react";
import { Link } from "react-router-dom";
import ProgressBar from "react-bootstrap/ProgressBar";
import { ThemeContext } from "../../App";
import { computeStats, buildHeatmap, computeStreak, longestStreak } from "../../utils/stats";
import DailyGoal from "./DailyGoal";
import FollowUps from "./FollowUps";
import "./dashboard.css";

// Format a duration in seconds as a compact human string.
function fmtDur(secs) {
	if (!secs) return "—";
	const m = Math.floor(secs / 60);
	const s = secs % 60;
	if (m === 0) return `${s}s`;
	if (s === 0) return `${m}m`;
	return `${m}m ${s}s`;
}

function StatCard({ label, value, sub, emoji }) {
	return (
		<div className="stat-card">
			<div className="stat-emoji">{emoji}</div>
			<div className="stat-value">{value}</div>
			<div className="stat-label">{label}</div>
			{sub ? <div className="stat-sub">{sub}</div> : null}
		</div>
	);
}

function heatColor(count, max, dark) {
	if (!count) return dark ? "#2c3038" : "#ebedf0";
	const levels = dark
		? ["#0e4429", "#006d32", "#26a641", "#39d353"]
		: ["#9be9a8", "#40c463", "#30a14e", "#216e39"];
	const ratio = max <= 1 ? 1 : count / max;
	const idx = Math.min(levels.length - 1, Math.floor(ratio * (levels.length - 1) + 0.0001));
	return levels[idx];
}

export default function Dashboard({ questionData, updateData, applyData }) {
	const dark = useContext(ThemeContext);
	const stats = computeStats(questionData);
	const { grid, max } = buildHeatmap(stats.solvedDates, 20);
	const streak = computeStreak(stats.solvedDates);
	const best = longestStreak(stats.solvedDates);

	const diff = stats.byDifficulty;
	const diffTotal = diff.Easy + diff.Medium + diff.Hard + diff.Unset || 1;

	const ts = stats.timeStats || { timedCount: 0, avgOverall: 0, avgByDifficulty: { Easy: 0, Medium: 0, Hard: 0 }, slowestTopics: [] };
	const recent = stats.recent || { last7: 0, last30: 0 };
	const maxDiffAvg = Math.max(ts.avgByDifficulty.Easy, ts.avgByDifficulty.Medium, ts.avgByDifficulty.Hard, 1);

	return (
		<div className={`dashboard ${dark ? "dash-dark" : ""}`}>
			<div className="page-header">
				<div className="page-emoji" role="img" aria-label="chart">
					📊
				</div>
				<h1 className="page-title">Dashboard</h1>
				<p className="page-sub">
					{stats.totalSolved} of {stats.totalQuestions} solved · {stats.percent}% complete
				</p>
			</div>

			<div className="stat-grid">
				<StatCard emoji="✅" label="Solved" value={`${stats.totalSolved}/${stats.totalQuestions}`} sub={`${stats.percent}% complete`} />
				<StatCard emoji="🔥" label="Current streak" value={`${streak}`} sub={streak === 1 ? "day" : "days"} />
				<StatCard emoji="🏆" label="Best streak" value={`${best}`} sub={best === 1 ? "day" : "days"} />
				<StatCard emoji="🔁" label="To revise" value={stats.revise} />
				<StatCard emoji="🔖" label="Bookmarked" value={stats.bookmarked} />
				<StatCard emoji="📝" label="With notes" value={stats.withNotes} />
				<StatCard emoji="✨" label="Custom added" value={stats.custom} sub={`${stats.customTopics} topics`} />
			</div>

			<div className="overall-progress">
				<ProgressBar variant="success" now={stats.percent} animated={stats.percent !== 100} label={`${stats.percent}%`} />
			</div>

			<DailyGoal questionData={questionData} updateData={updateData} />

			<FollowUps questionData={questionData} applyData={applyData} />

			<div className="dash-section">
				<h5>Solve-time analytics</h5>
				{ts.timedCount === 0 ? (
					<div className="an-hint">
						No timed solves yet. Use the timer in <Link to="/practice">Practice</Link> (or a{" "}
						<Link to="/mock">Mock interview</Link>) and your speed shows up here.
					</div>
				) : (
					<>
						<div className="an-cards">
							<div className="an-card">
								<div className="an-num">{fmtDur(ts.avgOverall)}</div>
								<div className="an-lbl">Avg solve time</div>
							</div>
							<div className="an-card">
								<div className="an-num">{ts.timedCount}</div>
								<div className="an-lbl">Timed solves</div>
							</div>
							<div className="an-card">
								<div className="an-num">{recent.last7}</div>
								<div className="an-lbl">Solved · 7 days</div>
							</div>
							<div className="an-card">
								<div className="an-num">{recent.last30}</div>
								<div className="an-lbl">Solved · 30 days</div>
							</div>
						</div>

						<div className="an-bars">
							{[
								["Easy", ts.avgByDifficulty.Easy],
								["Medium", ts.avgByDifficulty.Medium],
								["Hard", ts.avgByDifficulty.Hard],
							].map(([name, val]) => (
								<div className="an-bar-row" key={name}>
									<span className="an-bar-lbl">{name}</span>
									<div className="an-bar-track">
										<div className="an-bar-fill" style={{ width: `${(val / maxDiffAvg) * 100}%` }} />
									</div>
									<span className="an-bar-val">{fmtDur(val)}</span>
								</div>
							))}
						</div>

						{ts.slowestTopics.length ? (
							<div className="an-slowest">
								<div className="an-slowest-head">Slowest topics (avg)</div>
								{ts.slowestTopics.map((t) => (
									<div className="an-slowest-row" key={t.topicName}>
										<span className="an-slowest-name" title={t.topicName}>{t.topicName}</span>
										<span className="an-slowest-val">{fmtDur(t.avg)}</span>
									</div>
								))}
							</div>
						) : null}
					</>
				)}
			</div>

			<div className="dash-section">
				<h5>Difficulty mix</h5>
				<div className="difficulty-bar">
					{[
						["Easy", "#28a745", diff.Easy],
						["Medium", "#f0ad4e", diff.Medium],
						["Hard", "#dc3545", diff.Hard],
						["Unset", dark ? "#444b55" : "#cfd4da", diff.Unset],
					].map(([name, color, count]) => (
						<div
							key={name}
							className="difficulty-seg"
							style={{ width: `${(count / diffTotal) * 100}%`, background: color }}
							title={`${name}: ${count}`}
						/>
					))}
				</div>
				<div className="difficulty-legend">
					<span><i style={{ background: "#28a745" }} /> Easy {diff.Easy}</span>
					<span><i style={{ background: "#f0ad4e" }} /> Medium {diff.Medium}</span>
					<span><i style={{ background: "#dc3545" }} /> Hard {diff.Hard}</span>
					<span><i style={{ background: dark ? "#444b55" : "#cfd4da" }} /> Unset {diff.Unset}</span>
				</div>
			</div>

			<div className="dash-section">
				<h5>Activity (last 20 weeks)</h5>
				<div className="heatmap">
					{grid.map((week, wi) => (
						<div className="heat-col" key={wi}>
							{week.map((cell, di) => (
								<div
									key={di}
									className="heat-cell"
									title={cell.date ? `${cell.date}: ${cell.count} solved` : ""}
									style={{ background: cell.future ? "transparent" : heatColor(cell.count, max, dark) }}
								/>
							))}
						</div>
					))}
				</div>
				<div className="heat-legend">
					Less
					{[0, 1, 2, 3].map((l) => (
						<i key={l} style={{ background: heatColor(l === 0 ? 0 : Math.ceil((l / 3) * (max || 1)), max || 1, dark) }} />
					))}
					More
				</div>
			</div>

			<div className="dash-section">
				<h5>Per-topic progress</h5>
				{stats.byTopic.map((t) => (
					<Link key={t.slug} to={`/topic/${t.slug}`} className="topic-progress-row">
						<div className="tp-name">
							{t.topicName} {t.custom ? <span className="tp-custom">custom</span> : null}
						</div>
						<div className="tp-bar">
							<ProgressBar variant="success" now={t.percent} />
						</div>
						<div className="tp-count">
							{t.solved}/{t.total}
						</div>
					</Link>
				))}
			</div>
		</div>
	);
}
